import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type CachedHierarchyEntryResponseDto } from '@redteclab/api/clients/bully'
import { IconArrowLeft, IconCaretRight } from '@redteclab/icons'

import { DialogContent, DialogTitle } from 'base-ui'

import { cmsStoryblokSlugToPublicUrl } from '../../../../../cmsStoryblokSlugToPublicUrl'

import { useCategoriesNavigationContext } from './StoryblokCategoryNavigationProvider'

const categoryNavigationAccessorIsRootCategory = (
  category: CachedHierarchyEntryResponseDto,
): boolean => category.parent === null

export const StoryblokCategoryNavigationContent: FC<{
  categoryData?: CachedHierarchyEntryResponseDto | null
}> = ({ categoryData }) => {
  const { handleClose, isLoading, navigateToCategory } =
    useCategoriesNavigationContext()

  if (!categoryData) {
    return null
  }

  const isRootCategory = categoryNavigationAccessorIsRootCategory(categoryData)

  return (
    <>
      <DialogTitle
        className="min-h-14 items-center bg-light-brand-high"
        onClose={handleClose}
        slotProps={{ closeButton: { className: 'mr-5' } }}
      >
        {isRootCategory ? (
          <span className="pl-3.5">
            <FormattedMessage id="general.label.categories" />
          </span>
        ) : (
          <span className="flex items-center">
            <button
              className="flex flex-1 items-center justify-start gap-1 fill-dark-primary-max px-3.5 text-dark-primary-max"
              onClick={() => {
                if (!isLoading && categoryData.parent?.fullSlug) {
                  navigateToCategory(categoryData.parent.fullSlug, 'backward')
                }
              }}
              type="button"
            >
              <IconArrowLeft className="size-7" />
              <span>
                <FormattedMessage id="general.link.label.back" />
              </span>
            </button>
          </span>
        )}
      </DialogTitle>
      <DialogContent>
        {isRootCategory ? null : (
          <div
            className={clsx(
              'relative flex justify-between px-5 py-3.5 font-medium',
              'after:absolute after:inset-x-5 after:bottom-0 after:h-0 after:border-b after:border-dark-primary-min',
            )}
          >
            <a
              className="flex grow items-start"
              href={cmsStoryblokSlugToPublicUrl(categoryData.fullSlug)}
            >
              {categoryData.name}
            </a>
          </div>
        )}
        <ul>
          {categoryData.children
            ?.toSorted(
              (categoryA, categoryB) => categoryA.position - categoryB.position,
            )
            .map((category) => {
              return (
                <li
                  className={clsx(
                    isRootCategory ? 'px-5' : 'pl-11 pr-5',
                    'relative flex py-3.5',
                    'after:absolute after:inset-x-5 after:bottom-0 after:h-0 after:border-b after:border-dark-primary-min',
                  )}
                  key={category.akeneoCategoryCode}
                >
                  <a
                    className="flex grow items-start justify-between gap-x-2.5"
                    href={cmsStoryblokSlugToPublicUrl(category.fullSlug)}
                    onClick={(event) => {
                      if (isLoading || !category.hasChildren) {
                        return
                      }

                      event.preventDefault()
                      navigateToCategory(category.fullSlug, 'forward')
                    }}
                  >
                    <span className="flex w-full flex-row items-center gap-1">
                      {category.name}
                    </span>
                    {category.hasChildren ? (
                      <IconCaretRight className="size-6 shrink-0 grow text-dark-brand" />
                    ) : null}
                  </a>
                </li>
              )
            })}
        </ul>
      </DialogContent>
    </>
  )
}
